import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Waves } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassCard } from "@/components/GlassCard";
import { ErrorState } from "@/components/common/ErrorState";
import { Chip } from "@/components/common/Chips";
import { FeedCard } from "@/components/community/FeedCard";
import { useProfile } from "@/context/ProfileContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { getFeedApi, toggleKudosApi, toggleFollowApi } from "@/lib/api";

// Stable keys for skeleton placeholders (not array indices)
const SKELETON_KEYS = ["skeleton-feed-a", "skeleton-feed-b", "skeleton-feed-c"];

const EmptyCard = ({ text, testId }) => (
  <GlassCard className="p-8" data-testid={testId}>
    <div className="flex flex-col items-center text-center gap-3">
      <div className="h-12 w-12 rounded-2xl bg-cyan-300/10 border border-cyan-300/25 flex items-center justify-center">
        <Waves size={22} className="text-cyan-300/80" />
      </div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed max-w-xs">
        {text}
      </p>
    </div>
  </GlassCard>
);

export const FeedSection = () => {
  const { profile } = useProfile();
  const { t } = useLanguage();
  const [filter, setFilter] = useState("all");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(
    async (currentFilter) => {
      setLoading(true);
      setError(false);
      try {
        setPosts(await getFeedApi(profile.id, currentFilter));
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [profile.id]
  );

  useEffect(() => {
    load(filter);
  }, [filter, load]);

  const handleKudos = async (post) => {
    try {
      const res = await toggleKudosApi(post.id, profile.id);
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? { ...p, kudos_count: res.kudos_count, has_kudos: res.has_kudos }
            : p
        )
      );
    } catch {
      toast.error(t("community.loadError"));
    }
  };

  const handleFollow = async (post) => {
    try {
      const res = await toggleFollowApi(profile.id, post.profile_id);
      toast.success(
        res.following
          ? t("community.followedToast", { name: post.author_name })
          : t("community.unfollowedToast", { name: post.author_name })
      );
      if (filter === "following" && !res.following) {
        // Author disappears from the Following feed
        load(filter);
      } else {
        setPosts((prev) =>
          prev.map((p) =>
            p.profile_id === post.profile_id ? { ...p, is_following: res.following } : p
          )
        );
      }
    } catch {
      toast.error(t("community.loadError"));
    }
  };

  // Phase 10 (UGC): after blocking, immediately hide the author's posts
  const handleBlocked = (blockedProfileId) => {
    setPosts((prev) => prev.filter((p) => p.profile_id !== blockedProfileId));
  };

  if (error) {
    return (
      <ErrorState
        message={t("community.loadError")}
        onRetry={() => load(filter)}
        testId="community-retry-button"
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Chip
          testId="community-filter-all"
          selected={filter === "all"}
          onClick={() => setFilter("all")}
        >
          {t("community.filterAll")}
        </Chip>
        <Chip
          testId="community-filter-following"
          selected={filter === "following"}
          onClick={() => setFilter("following")}
        >
          {t("community.filterFollowing")}
        </Chip>
      </div>

      {loading ? (
        <div className="space-y-3">
          {SKELETON_KEYS.map((key) => (
            <Skeleton key={key} className="h-36 rounded-2xl bg-white/10" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        filter === "following" ? (
          <EmptyCard text={t("community.emptyFollowing")} testId="community-empty-following" />
        ) : (
          <EmptyCard text={t("community.emptyFeed")} testId="community-empty-feed" />
        )
      ) : (
        <div className="space-y-3" data-testid="community-feed-list">
          {posts.map((post) => (
            <FeedCard
              key={post.id}
              post={post}
              onKudos={handleKudos}
              onFollow={handleFollow}
              onBlocked={handleBlocked}
            />
          ))}
        </div>
      )}
    </div>
  );
};
